import chalk from 'chalk';
import dateFormat from 'dateformat';
import deepCleaner from 'deep-cleaner';
import ProgressBar from 'progress';
import { tryStringify } from './tryparse';

type LogMethod = (content: string, print?: boolean) => void;
type LogJsonMethod = (content: any) => void;
type LogArrayMethod = (contentArray: string[]) => void;
type LogErrorFunc = (
  err: any,
  msg?: string,
  level?: 'error' | 'critical'
) => void;

export type LoggerLevels =
  | boolean
  | 'none'
  | 'error'
  | 'warning'
  | 'info'
  | 'debug';

let level: LoggerLevels = 'warning';

export class Logger {
  static setLevel(logLevel: LoggerLevels) {
    level = logLevel;
  }
  static shouldLog = (me: LoggerLevels) => {
    if (level === me || process.env.DEBUG === 'true') return true;
    switch (me) {
      case 'debug':
        if (level === 'info' || level === true) return false;
      case 'info':
        if (level === 'warning') return false;
      case 'warning':
        if (level === 'error') return false;
      case 'error':
        if (level === 'none' || level === false) return false;
    }
    return true;
  };
  static getDatePrefix = () => {
    return `[${dateFormat(new Date(), 'dd/mm HH:MM:ss')}]`;
  };
  static errorText = chalk.bold.red;
  static warningText = chalk.keyword('orange');
  static successText = chalk.keyword('green');
  static helpText = chalk.blue;
  static infoText = chalk.keyword('grey');
  static standardText = chalk.keyword('white');
  static highlightText = chalk.keyword('yellow');
  static boldText = chalk.bold;
  static critical: LogMethod = (content) => {
    const message = `${Logger.getDatePrefix()} [CRITICAL] ${Logger.errorText(
      content
    )}`;
    console.log(message);
  };
  static error: LogMethod = (content) => {
    const message = `${Logger.getDatePrefix()} [ERROR] ${Logger.errorText(
      content
    )}`;
    if (progress.active) progress.current.interrupt(message);
    else console.log(message);
  };
  static warning: LogMethod = (content) => {
    if (Logger.shouldLog('warning')) {
      const message = `${Logger.getDatePrefix()} [WARN] ${Logger.warningText(
        content
      )}`;
      if (progress.active) progress.current.interrupt(message);
      else console.log(message);
    }
  };
  static success: LogMethod = (content, print = false) => {
    if (print || Logger.shouldLog('info')) {
      const message = `${Logger.getDatePrefix()} [OK] ${Logger.successText(
        content
      )}`;
      if (progress.active) progress.current.interrupt(message);
      else console.log(message);
    }
  };
  static info: LogMethod = (content, print = false) => {
    if (print || Logger.shouldLog('info')) {
      const message = `${Logger.getDatePrefix()} [INFO] ${Logger.infoText(
        content
      )}`;
      if (progress.active) progress.current.interrupt(message);
      else console.log(message);
    }
  };
  static standard: LogMethod = (content) => {
    const message = `${Logger.getDatePrefix()} [STD] ${Logger.standardText(
      content
    )}`;
    if (progress.active) progress.current.interrupt(message);
    else console.log(message);
  };
  static debug: LogMethod = (content) => {
    if (Logger.shouldLog('debug')) {
      const message = `${Logger.getDatePrefix()} [DEBUG] ${Logger.infoText(
        content
      )}`;
      if (progress.active) progress.current.interrupt(message);
      else console.log(message);
    }
  };
  static json: LogJsonMethod = (content, depth = 9) =>
    console.dir(deepCleaner(content), { colors: true, depth });
  static mixed: LogArrayMethod = (contentArray) =>
    console.log(`${Logger.getDatePrefix()} ${contentArray.join(' ')}`);
  static line = () =>
    Logger.raw(`  ${Logger.infoText(`-------------------------------------`)}`);
  static raw: LogMethod = (content: string) => {
    if (progress.active) progress.current.interrupt(content);
    else console.log(content);
  };
}

export const logError: LogErrorFunc = (
  err = new Error('Undefined error'),
  msg,
  level = 'error'
) => {
  Logger[level](msg || err.message || err?.data?.message || err.Message);
  (Array.isArray(err) ? err : [err]).map((error: AppError) => {
    if ('field' in error && 'message' in error)
      Logger.raw(`  ${Logger.infoText(`${error.field}: ${error.message}`)}`);
    if ('stack' in error) Logger.raw(`  ${Logger.infoText(error.stack)}`);
    if ('data' in error && error.data)
      Logger.raw(`  ${Logger.infoText(tryStringify(error.data))}`);
  });
  Logger.line();
  return null;
};

export const progress = {
  active: false,
  done: () => new ProgressBar('', 0),
  colours: { green: '\u001b[42m \u001b[0m', red: '\u001b[41m \u001b[0m' },
  current: new ProgressBar(`:bar`, {
    complete: '=',
    incomplete: ' ',
    width: 20,
    total: 0,
  }),
};

export const newProgressBar = (
  format: string,
  options: ProgressBar.ProgressBarOptions & { output?: boolean }
) => {
  const { output, ...progressOptions } = options;
  if (output && process.stdout.isTTY) {
    const defaultOptions: Partial<ProgressBar.ProgressBarOptions> = {
      complete: ' ',
      head: Logger.successText('>>'),
      incomplete: progress.colours.red,
      width: 40,
    };
    progress.current = new ProgressBar(format, {
      ...defaultOptions,
      ...progressOptions,
    });
    progress.active = true;
    return progress.current;
  }
  progress.active = false;
  return undefined;
};

export const completeProgressBar = () => {
  progress.current = progress.done();
  progress.active = false;
};
